(() => {
  if (customElements.get('kanama-variant-media')) return;
  class VariantMediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.version = 0;
      this.abort = new AbortController();
      this.main = this.querySelector('[data-kvm-main]');
      this.mode = this.main.dataset.kvmMode === 'video' ? 'video' : 'image';
      this.images = this.querySelector('[data-kvm-images]');
      this.videoEntry = this.querySelector('[data-kvm-video-entry]');
      this.dialog = this.querySelector('[data-kvm-dialog]');
      this.container = this.closest('[data-product-featured]');
      this.templates = new Map([...this.querySelectorAll('template[data-kvm-item]')].map(el => [el.dataset.kvmItem, el]));
      this.cache = new Map();
      try { this.map = JSON.parse(this.querySelector('[data-kvm-map]').textContent); }
      catch (_) { this.active = false; return; }
      this.main.dataset.kvmReady = 'true';
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.previewVisible = false;
      this.previewObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
        this.previewVisible = entries.some(entry => entry.isIntersecting);
        this.updatePreview();
      }, {threshold: 0.2}) : null;
      const signal = this.abort.signal;
      this.addEventListener('click', event => this.onClick(event), {signal});
      // Some theme swatches update the product form and URL without allowing the
      // jQuery `variant:changed` event to reach document. Reconcile after any
      // interaction in the owning product so the gallery cannot get stranded on
      // the previous variant.
      this.container?.addEventListener('change', () => this.scheduleVariantSync(), {signal});
      this.container?.addEventListener('click', () => this.scheduleVariantSync(), {signal});
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAll();
        else this.updatePreview();
      }, {signal});
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.pauseAll();
        this.updatePreview();
      }, {signal});
      this.dialog.addEventListener('close', () => this.querySelector('[data-kvm-dialog-image]').replaceChildren(), {signal});
      this.bindTheme();
      if (!this.hydrateInitialVariant(this.map.initialVariantId)) this.setVariant(this.map.initialVariantId, true);
      window.addEventListener('load', () => { this.bindTheme(); this.buildCarousel(); }, {signal, once: true});
      this.readyTimer = setInterval(() => {
        this.bindTheme();
        this.buildCarousel();
        if (this.bound && (this.carousel || this.activeIds.length < 2)) clearInterval(this.readyTimer);
      }, 200);
      this.stopTimer = setTimeout(() => clearInterval(this.readyTimer), 15000);
    }
    bindTheme() {
      if (this.bound || !this.container || !window.jQuery_T4NT) return;
      this.bound = true;
      this.handler = event => {
        const variantId = event.currentVariant ? String(event.currentVariant.id) : '';
        if (!Object.prototype.hasOwnProperty.call(this.map.variants, variantId)) return;
        this.setVariant(variantId);
      };
      // Bind the owning product directly as well as document. The theme's bundled
      // jQuery does not consistently bubble custom events to document.
      this.themeTargets = window.jQuery_T4NT(this.container).add(document);
      this.themeTargets.on('variant:changed.kanamaMedia', this.handler);
    }
    selectedVariantId() {
      const selectors = [
        'form[action*="/cart/add"] select[name="id"]',
        'select[name="id"][data-product-featured]',
        'select[name="id"]'
      ];
      for (const selector of selectors) {
        const value = this.container?.querySelector(selector)?.value;
        if (value && Object.prototype.hasOwnProperty.call(this.map.variants, String(value))) return String(value);
      }
      const urlId = new URL(window.location.href).searchParams.get('variant');
      return urlId && Object.prototype.hasOwnProperty.call(this.map.variants, urlId) ? urlId : '';
    }
    scheduleVariantSync() {
      cancelAnimationFrame(this.variantSyncFrame);
      clearTimeout(this.variantSyncTimer);
      const sync = () => {
        const variantId = this.selectedVariantId();
        if (variantId) this.setVariant(variantId);
      };
      this.variantSyncFrame = requestAnimationFrame(() => {
        this.variantSyncFrame = requestAnimationFrame(sync);
      });
      // Keep a short delayed pass for themes that update their hidden variant
      // selector asynchronously after the visible swatch click.
      this.variantSyncTimer = setTimeout(sync, 150);
    }
    disconnectedCallback() {
      this.active = false;
      this.version++;
      clearInterval(this.readyTimer);
      clearTimeout(this.stopTimer);
      clearTimeout(this.variantSyncTimer);
      cancelAnimationFrame(this.variantSyncFrame);
      this.abort?.abort();
      this.previewObserver?.disconnect();
      this.pauseAll();
      this.carousel?.destroy();
      if (this.bound) this.themeTargets?.off('variant:changed.kanamaMedia', this.handler);
      this.carousel = null;
      this.bound = false;
    }
    mediaType(id) {
      return this.cache.get(id)?.slide?.dataset.kvmType
        || this.templates.get(id)?.content.querySelector('.kvm-slide')?.dataset.kvmType;
    }
    variantMediaIds(variantId) {
      return [...new Set(this.map.variants[String(variantId)] || [])].filter(id => this.templates.has(id));
    }
    hydrateInitialVariant(variantId) {
      const normalizedId = String(variantId || '');
      const mediaIds = this.variantMediaIds(normalizedId);
      const slides = [...this.main.querySelectorAll(':scope > .kvm-slide')];
      const slideById = new Map(slides.map(slide => [slide.dataset.kvmId, slide]));
      const thumbById = new Map([...this.images.querySelectorAll('.kvm-thumb[data-kvm-id]')].map(thumb => [thumb.dataset.kvmId, thumb]));
      const videoSwitchById = new Map([...this.videoEntry.querySelectorAll('[data-kvm-video-switch][data-kvm-id]')].map(button => [button.dataset.kvmId, button]));
      const imageIds = mediaIds.filter(id => this.mediaType(id) === 'image');
      const videoIds = mediaIds.filter(id => this.mediaType(id) === 'video');
      let mode = this.mode;
      if (mode === 'video' && !videoIds.length) mode = 'image';
      if (mode !== 'video' && !imageIds.length) mode = 'video';
      const activeIds = mode === 'video' ? videoIds : imageIds;
      const renderedIds = slides.map(slide => slide.dataset.kvmId);
      if (activeIds.length !== renderedIds.length || activeIds.some((id, index) => id !== renderedIds[index])) return false;

      for (const id of mediaIds) {
        const slide = slideById.get(id);
        const thumb = thumbById.get(id);
        const videoSwitch = videoSwitchById.get(id);
        if (slide || thumb || videoSwitch) this.cache.set(id, {slide, thumb, videoSwitch});
      }

      this.currentVariantId = normalizedId;
      this.mode = mode;
      this.imageIds = imageIds;
      this.videoIds = videoIds;
      this.activeIds = activeIds;
      this.selected = activeIds[0];
      this.selectedByMode = {image: imageIds[0], video: videoIds[0]};
      this.main.dataset.kvmMode = mode;
      this.querySelector('[data-kvm-desktop-controls]').hidden = mediaIds.length === 0;
      this.querySelector('[data-kvm-empty]').hidden = mediaIds.length > 0;
      this.querySelector('[data-kvm-stage]').hidden = mediaIds.length === 0;
      this.querySelector('[data-kvm-thumbnails]').hidden = imageIds.length === 0;
      this.videoEntry.hidden = videoIds.length === 0;
      this.querySelector('[data-kvm-navigation]').hidden = activeIds.length < 2;
      const videoSwitch = this.videoEntry.querySelector('[data-kvm-video-switch]');
      if (videoSwitch) this.previewObserver?.observe(videoSwitch);
      this.buildCarousel();
      if (this.selected) this.select(this.selected, false);
      return true;
    }
    media(id) {
      let item = this.cache.get(id);
      const type = item?.slide?.dataset.kvmType
        || this.templates.get(id)?.content.querySelector('.kvm-slide')?.dataset.kvmType;
      const isIncomplete = !item?.slide || (type === 'image' ? !item?.thumb : !item?.videoSwitch);
      if (isIncomplete) {
        const fragment = this.templates.get(id)?.content.cloneNode(true);
        if (fragment) {
          item = {
            slide: item?.slide || fragment.querySelector('.kvm-slide'),
            thumb: item?.thumb || fragment.querySelector('.kvm-thumb'),
            videoSwitch: item?.videoSwitch || fragment.querySelector('.kvm-video-switch')
          };
          this.cache.set(id, item);
        }
      }
      return item;
    }
    pauseAll() {
      this.querySelectorAll('video').forEach(video => {
        video.pause();
        const playButton = video.closest('.kvm-main-video-wrap')?.querySelector('[data-kvm-video-play]');
        if (playButton) playButton.hidden = false;
      });
    }
    releaseRenderedMedia() {
      for (const video of this.querySelectorAll('.kvm-main video, .kvm-desktop-controls video')) {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      }
      for (const image of this.querySelectorAll('.kvm-main img, .kvm-desktop-controls img')) {
        image.removeAttribute('srcset');
        image.removeAttribute('src');
      }
      this.main.replaceChildren();
      this.images.replaceChildren();
      this.videoEntry.replaceChildren();
      this.cache.clear();
    }
    setVariant(variantId, force = false) {
      const normalizedId = String(variantId || '');
      if (!force && normalizedId === this.currentVariantId) return;
      this.currentVariantId = normalizedId;
      this.version++;
      this.pauseAll();
      if (this.dialog.open) this.dialog.close();
      this.previewObserver?.disconnect();
      this.previewVisible = false;
      this.carousel?.destroy();
      this.carousel = null;
      this.releaseRenderedMedia();
      const mediaIds = this.variantMediaIds(normalizedId);
      this.imageIds = mediaIds.filter(id => this.mediaType(id) === 'image');
      this.videoIds = mediaIds.filter(id => this.mediaType(id) === 'video');
      this.mode = this.imageIds.length ? 'image' : (this.videoIds.length ? 'video' : 'image');
      this.selectedByMode = {
        image: this.imageIds[0],
        video: this.videoIds[0]
      };
      this.renderDesktopControls();
      this.querySelector('[data-kvm-desktop-controls]').hidden = mediaIds.length === 0;
      this.querySelector('[data-kvm-empty]').hidden = mediaIds.length > 0;
      this.querySelector('[data-kvm-stage]').hidden = mediaIds.length === 0;
      if (!mediaIds.length) {
        this.activeIds = [];
        this.selected = undefined;
        this.main.replaceChildren();
        this.querySelector('[data-kvm-status]').textContent = '';
        this.querySelector('[data-kvm-navigation]').hidden = true;
        return;
      }
      this.setMode(this.mode, false);
    }
    renderDesktopControls() {
      const thumbs = this.imageIds.map(id => this.media(id)?.thumb).filter(Boolean);
      this.images.replaceChildren(...thumbs);
      this.querySelector('[data-kvm-thumbnails]').hidden = thumbs.length === 0;
      const videoSwitch = this.videoIds.length ? this.media(this.videoIds[0])?.videoSwitch : null;
      this.videoEntry.replaceChildren(...(videoSwitch ? [videoSwitch] : []));
      this.videoEntry.hidden = !videoSwitch;
      if (videoSwitch) this.previewObserver?.observe(videoSwitch);
    }
    setMode(mode, remember = true) {
      const ids = mode === 'video' ? this.videoIds : this.imageIds;
      if (!ids.length) return;
      this.version++;
      this.pauseAll();
      this.carousel?.destroy();
      this.carousel = null;
      this.mode = mode;
      this.activeIds = ids;
      this.selected = this.selectedByMode[mode] || ids[0];
      this.main.dataset.kvmMode = mode;
      this.main.replaceChildren(...ids.map(id => this.media(id).slide));
      for (const button of this.querySelectorAll('[data-kvm-mode-switch]')) button.setAttribute('aria-pressed', String(button.dataset.kvmModeSwitch === mode));
      this.videoEntry.querySelector('[data-kvm-video-switch]')?.setAttribute('aria-pressed', String(mode === 'video'));
      this.querySelector('[data-kvm-navigation]').hidden = ids.length < 2;
      this.buildCarousel();
      this.select(this.selected, false);
      if (mode === 'video' && remember) this.playActiveVideo();
    }
    buildCarousel() {
      if (!this.active || this.carousel || this.activeIds.length < 2 || !window.Flickityt4s) return;
      for (const slide of this.main.children) slide.hidden = false;
      this.carousel = new window.Flickityt4s(this.main, {
        cellSelector: '.kvm-slide', cellAlign: 'left', contain: true,
        pageDots: false, prevNextButtons: false, adaptiveHeight: false,
        wrapAround: false, draggable: true,
        initialIndex: Math.max(0, this.activeIds.indexOf(this.selected)),
        rightToLeft: document.documentElement.dir === 'rtl'
      });
      this.carousel.on('change', index => {
        this.select(this.activeIds[index], false);
      });
      this.main.querySelectorAll('img').forEach(img => {
        if (!img.complete) img.addEventListener('load', () => this.carousel?.resize(), {once: true, signal: this.abort.signal});
      });
    }
    select(id, sync = true) {
      if (!this.activeIds.includes(id)) return;
      const selectionChanged = this.selected !== id;
      if (selectionChanged && this.mode === 'video') this.pauseAll();
      this.selected = id;
      this.selectedByMode[this.mode] = id;
      const index = this.activeIds.indexOf(id);
      if (sync && this.carousel) this.carousel.select(index);
      for (const [key, item] of this.cache) {
        if (item.thumb) item.thumb.setAttribute('aria-pressed', String(this.mode === 'image' && key === id));
        if (item.slide) {
          item.slide.hidden = !this.carousel && key !== id;
          item.slide.inert = key !== id;
          item.slide.setAttribute('aria-hidden', String(key !== id));
        }
      }
      this.querySelector('[data-kvm-status]').textContent = `${index + 1} / ${this.activeIds.length}`;
      if (this.mode === 'image') {
        const selectedImage = this.media(id)?.slide?.querySelector('img');
        if (selectedImage) selectedImage.loading = 'eager';
        const thumb = this.media(id)?.thumb;
        if (thumb) {
          const offset = this.images.scrollLeft + thumb.getBoundingClientRect().left - this.images.getBoundingClientRect().left - (this.images.clientWidth - thumb.clientWidth) / 2;
          this.images.scrollTo?.({left: offset, behavior: this.motion.matches ? 'instant' : 'smooth'});
        }
      }
      if (selectionChanged && this.mode === 'video') this.playActiveVideo();
    }
    playActiveVideo() {
      const video = this.media(this.selected)?.slide?.querySelector('.kvm-main-video');
      if (!video) return;
      const playButton = video.closest('.kvm-main-video-wrap')?.querySelector('[data-kvm-video-play]');
      if (playButton) playButton.hidden = true;
      video.muted = true;
      const version = this.version;
      video.play()?.then(() => {
        if (!this.active || document.hidden || version !== this.version || this.mode !== 'video' || this.selected !== video.closest('[data-kvm-id]')?.dataset.kvmId) video.pause();
      }).catch(() => {
        if (playButton) playButton.hidden = false;
      });
    }
    updatePreview() {
      const host = this.videoEntry.querySelector('[data-kvm-video-switch]');
      if (!host) return;
      let video = host.querySelector('video');
      const allowed = this.active && !document.hidden && !this.motion.matches && this.previewVisible;
      if (allowed && !video) {
        const template = host.querySelector('template');
        if (template) host.querySelector('.kvm-video-preview').append(template.content.cloneNode(true));
        video = host.querySelector('video');
      }
      if (!video) return;
      if (!allowed) { video.pause(); return; }
      video.muted = true;
      video.play()?.catch(() => {});
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.matches('.kvm-thumb')) {
        if (this.mode !== 'image') this.setMode('image');
        this.select(button.dataset.kvmId);
      } else if (button.matches('[data-kvm-video-switch]')) this.setMode('video');
      else if (button.matches('[data-kvm-mode-switch]')) this.setMode(button.dataset.kvmModeSwitch);
      else if (button.matches('[data-kvm-video-play]')) this.playActiveVideo();
      else if (button.matches('[data-kvm-previous],[data-kvm-next]')) {
        const delta = button.hasAttribute('data-kvm-next') ? 1 : -1;
        const index = this.activeIds.indexOf(this.selected);
        this.select(this.activeIds[(index + delta + this.activeIds.length) % this.activeIds.length]);
      } else if (button.matches('[data-kvm-scroll]')) {
        const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
        this.images.scrollBy({left: direction * Number(button.dataset.kvmScroll) * this.images.clientWidth * 0.8, behavior: this.motion.matches ? 'instant' : 'smooth'});
      } else if (button.matches('[data-kvm-zoom]')) {
        this.pauseAll();
        this.querySelector('[data-kvm-dialog-image]').replaceChildren(button.querySelector('img').cloneNode());
        this.dialog.showModal();
      } else if (button.matches('[data-kvm-close]')) this.dialog.close();
    }
  }
  customElements.define('kanama-variant-media', VariantMediaGallery);
})();
